import { Calendar, Heart, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import placeholder from "../imagens/esperandoMemoria.png";
import { AlbumDTO } from "../models/DTOs/AlbumDTO";
import { listAlbums } from "../services/AlbumService";

const Albums = () => {
  const [albums, setAlbums] = useState<AlbumDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Exemplo — adapte para seu service
    const fetchAlbums = async () => {
      try {
        setAlbums(await listAlbums());
      } catch (error) {
        // Se der erro (ex: 401 + redirect), garantimos array vazio
        setAlbums([]);
      } finally {
        setLoading(false);
      }
    }
          
    fetchAlbums();
  }, []);

  const isLocked = (album: AlbumDTO) => {
    if (!album.trancadoAte) return false;
    return new Date(album.trancadoAte) > new Date();
  };

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="flex justify-center mb-4">
              <Heart
                className="w-12 h-12 text-primary animate-float"
                fill="currentColor"
              />
            </div>

            <h1 className="font-romantic text-5xl md:text-7xl text-foreground mb-4">
              Nossos Álbuns
            </h1>

            <p className="font-serif text-xl text-muted-foreground">
              Coleções de momentos que guardamos com carinho
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {albums.map((album, index) => {
              const locked = isLocked(album);
              const CardWrapper = locked ? "div" : Link;

              const cardProps = locked
                ? { className: "group animate-fade-in cursor-not-allowed" }
                : {
                    to: `/album/${album.id}`,
                    className: "group animate-fade-in",
                  };

              return (
                <CardWrapper className="relative max-h-[90vh]"
                  key={album.id}
                  {...(cardProps as any)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div
                    className={`bg-card rounded-2xl overflow-hidden shadow-lg transition-all duration-300 border-4 border-white ${
                      locked
                        ? "opacity-90"
                        : "hover:shadow-2xl hover:scale-105"
                    }`}
                  >
                    {/* Capa */}
                    <div className="relative aspect-square">
                      <img
                        src={album.capaUrl || placeholder}
                        alt={album.nome}
                        className={`w-full object-cover transition-transform duration-500 ${
                          locked
                            ? "grayscale blur-[2px]"
                            : "group-hover:scale-100"
                        }`}
                      />

                      {locked ? (
                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                          <div className="bg-card/90 backdrop-blur-sm rounded-full p-6 mb-4 shadow-xl border-2 border-primary/30">
                            <Lock className="w-10 h-10 text-primary" />
                          </div>
                          <p className="font-romantic text-white text-lg">
                            Álbum Trancado
                          </p>
                        </div>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-6 bg-gradient-romantic">
                      <h3 className="font-romantic text-2xl text-foreground mb-2">
                        {album.nome}
                      </h3>

                      {locked ? (
                        <div className="flex items-center gap-2 text-sm text-primary">
                          <Lock className="w-4 h-4" />
                          <span className="font-serif">
                            Libera em:{" "}
                            {new Date(album.trancadoAte!).toLocaleDateString(
                              "pt-BR"
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span className="font-serif">
                            {formatDate(album.criadoEm)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardWrapper>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-card/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border-2 border-primary/20">
              <p className="font-romantic text-3xl text-primary mb-2">
                Cada foto conta nossa história ♡
              </p>
              <p className="font-serif text-muted-foreground">
                Memórias que guardaremos para sempre
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Albums;
